import type { SelectOption } from '@aus-platform/design-system';
import {
  Button,
  ButtonVariant,
  DropDownButtonDirection,
  IconIdentifier,
  Placement,
  Tooltip,
} from '@aus-platform/design-system';
import find from 'lodash/find';
import isNull from 'lodash/isNull';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import type { WorkspaceLayer } from 'src/map-3d/components/map-3d-sidebar/shared';
import { isEllipsis } from 'src/shared/helpers';
import { PolygonDatasetDropdownItem } from './polygon-dataset-dropdown-item';

type PolygonDatasetDropdownProps = {
  options: SelectOption<WorkspaceLayer>[];
  selectedId: string | null;
  handleOrthoChange: (orthoLayer: WorkspaceLayer) => void;
};

const PLACEHOLDER = 'Select dataset';

export const PolygonDatasetDropdown: React.FC<PolygonDatasetDropdownProps> = ({
  options,
  selectedId,
  handleOrthoChange,
}) => {
  const selected =
    find(options, (option) => option.value.id === selectedId) ?? null;

  const btnText = selected?.label ?? PLACEHOLDER;

  const triggerTextRef = useRef<HTMLDivElement>(null);
  const [isSelectedNameOverflowing, setIsSelectedNameOverflowing] =
    useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useLayoutEffect(() => {
    setIsSelectedNameOverflowing(isEllipsis(triggerTextRef));
  }, [btnText]);

  return (
    <Dropdown
      className="polygon-dataset-dropdown"
      drop={DropDownButtonDirection.Up}
      onToggle={(next) => {
        setIsMenuOpen(next);
        if (next) {
          setIsHovered(false);
        }
      }}
      align={DropDownButtonDirection.End}
    >
      <Dropdown.Toggle
        className="polygon-dataset-dropdown__toggle"
        as="div"
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip
          placement={Placement.Top}
          className="polygon-dataset-dropdown__tooltip"
          show={isHovered && isSelectedNameOverflowing && !isMenuOpen}
          hoverText={isNull(selected) ? btnText : selected.value.name}
        >
          <Button
            variant={ButtonVariant.Outline}
            rightIconIdentifier={IconIdentifier.CaretDownFill}
            iconSize={24}
          >
            <div
              ref={triggerTextRef}
              className="polygon-dataset-dropdown__toggle-text"
            >
              {btnText}
            </div>
          </Button>
        </Tooltip>
      </Dropdown.Toggle>
      <Dropdown.Menu className="polygon-dataset-dropdown__menu" flip={false}>
        {/* Another wrapper required to match scrollbar background with the menu's. */}
        <div className="polygon-dataset-dropdown__menu-wrapper">
          {options.map((opt) => (
            <PolygonDatasetDropdownItem
              key={opt.value.id}
              isSelected={opt.value.id === selectedId}
              option={opt}
              handleOrthoChange={handleOrthoChange}
            />
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};
